import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { servicesAPI, songsAPI, teamAPI, serviceTypesAPI, rolesAPI, groupsAPI, serviceChatAPI, availabilityAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import SongPickerDialog from '../components/SongPickerDialog';
import ItemDetailView from '../components/ItemDetailView';
import OrderItemRow from '../components/OrderItemRow';
import DeleteButton from '../components/ui/DeleteButton';
import InfoTab from '../components/InfoTab';
import PeopleTab from '../components/PeopleTab';
import ProgramViewerMode from '../components/ProgramViewerMode';
import ProgramPrintModal from '../components/ProgramPrintModal';
import ServiceChatTab from '../components/ServiceChatTab';
import SetlistTab from '../components/SetlistTab';
import { ArrowLeft, Plus, Trash2, GripVertical, FileText, Pencil, Bell, Info, LayoutList, Users, MessageSquare, ListMusic, Calendar, Clock, MapPin, Layers, Printer, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { canEditService } from '../utils/permissions';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const TABS = [
  { id: 'info',     label: 'Info',       icon: Info },
  { id: 'order',    label: 'Program',    icon: LayoutList },
  { id: 'people',   label: 'People',     icon: Users },
  { id: 'setlist',  label: 'Setlist',    icon: ListMusic },
  { id: 'chat',     label: 'Discussion', icon: MessageSquare },
];

const normalizeItem = (item) => {
  if (item.type !== 'item') return item;
  const out = { ...item };
  if (out.title !== undefined && out.name === undefined) { out.name = out.title; delete out.title; }
  if (out.item_type === 'song' && out.song_id) {
    if (!out.songs || out.songs.length === 0) out.songs = [{ song_id: out.song_id }];
    delete out.song_id;
  }
  if (!out.songs) out.songs = [];
  return out;
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [songs, setSongs] = useState([]);
  const [team, setTeam] = useState([]);
  const [blockedMemberIds, setBlockedMemberIds] = useState(new Set());
  const [deletedMemberIds, setDeletedMemberIds] = useState(new Set());
  const [serviceTypes, setServiceTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // History-based navigation: reset state on service change so modal/item-detail back
  // handling starts clean. Tab navigation now uses URL hash (Option C).
  useEffect(() => {
    initialMountRef.current = true;
    window.history.replaceState({}, '');
  }, [serviceId]);
  useEffect(() => {
    const handlePopState = (event) => {
      // If this popstate was triggered by our own history.back() call (modal close), ignore it.
      if (popstateSuppressedRef.current) {
        popstateSuppressedRef.current = false;
        return;
      }
      // If a modal/dialog is open, close it instead of navigating away.
      if (closeAnyOpenModalRef.current?.()) {
        modalHistoryPushedRef.current = false;
        return;
      }
      // If a program item detail is open, close it (back from item detail view).
      if (selectedItemIdRef.current) {
        setSelectedItemId(null);
        return;
      }
      // Tab navigation is handled by React Router's location.hash — no further action needed.
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [serviceId]);
  // Sync activeTab with URL hash (Option C: hash-based tab navigation).
  // Fires on mount (if URL has a hash) and whenever the hash changes (tab click, back/forward).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hash = location.hash.slice(1); // remove leading '#'
    const validTab = TABS.find(t => t.id === hash);
    if (validTab) {
      initialMountRef.current = false;
      tabRestoredRef.current = true;
      setActiveTab(validTab.id);
      serviceChatAPI.setUserState(serviceId, { last_tab: validTab.id }).catch(() => {});
      if (validTab.id !== 'order') setSelectedItemId(null);
      if (validTab.id === 'chat') {
        serviceChatAPI.markRead(serviceId).catch(() => {});
        setDiscUnreadCount(0);
        if (getUnreadAbortRef.current) getUnreadAbortRef.current.cancelled = true;
        // Bug 1 fix: persist locally so Services.js clears the badge even in the race window
        try {
          const cleared = JSON.parse(sessionStorage.getItem('clearedDiscussions') || '{}');
          cleared[serviceId] = Date.now();
          sessionStorage.setItem('clearedDiscussions', JSON.stringify(cleared));
        } catch { /* quota */ }
      }
    } else if (!initialMountRef.current) {
      // Hash was cleared after a navigation (e.g. back from #chat → base URL) → go to info.
      setActiveTab('info');
      serviceChatAPI.setUserState(serviceId, { last_tab: 'info' }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, serviceId]);
  // Prevents the DB-restored tab from overriding a deliberate user click
  // that happens before the async getUnread response arrives.
  const tabRestoredRef = useRef(false);
  // Keeps activeTab accessible in touch event handlers without stale closures.
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  // Tracks selectedItemId in a ref (for popstate handler which would have a stale closure otherwise).
  // NOTE: ref + sync effect must be declared AFTER the selectedItemId state to avoid TDZ error.
  const selectedItemIdRef = useRef(null);
  // True until the first hash-based tab navigation occurs; prevents hash '' from
  // resetting the tab on the initial page load (before DB restoration fires).
  const initialMountRef = useRef(true);
  const [formData, setFormData] = useState({
    name: '', service_type: '', date: '', time: '', notes: '',
    order: [], team_assignments: [], setlist: [],
  });
  const [isAddSectionDialogOpen, setIsAddSectionDialogOpen] = useState(false);
  const [addSectionName, setAddSectionName] = useState('');
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const [addItemSectionId, setAddItemSectionId] = useState(null);
  const [addItemName, setAddItemName] = useState('');
  const [addItemNotes, setAddItemNotes] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(null);
  // Keep selectedItemIdRef in sync with state (must be after selectedItemId declaration to avoid TDZ)
  useEffect(() => { selectedItemIdRef.current = selectedItemId; }, [selectedItemId]);
  const [songPickerOpen, setSongPickerOpen] = useState(false);
  const [isEditSectionDialogOpen, setIsEditSectionDialogOpen] = useState(false);
  const [editSectionId, setEditSectionId] = useState(null);
  const [editSectionName, setEditSectionName] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [notifyingChange, setNotifyingChange] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [myAssignment, setMyAssignment] = useState(null);
  const [respondingAction, setRespondingAction] = useState(null); // 'accept' | 'decline'
  const [discUnreadCount, setDiscUnreadCount] = useState(0);
  // Cancellation token: prevents stale getUnread API responses from overwriting a user-cleared badge
  const getUnreadAbortRef = useRef(null);
  // Modal history: used to close dialogs with the browser back button
  const popstateSuppressedRef = useRef(false); // suppress popstate triggered by our own history.back()
  const modalHistoryPushedRef = useRef(false);  // true when we pushed a history entry for an open modal
  const closeAnyOpenModalRef = useRef(null);    // always points to the latest "close any open modal" fn
  const [isAddLinkDialogOpen, setIsAddLinkDialogOpen] = useState(false);
  const [addLinkUrl, setAddLinkUrl] = useState('');
  const [addLinkLabel, setAddLinkLabel] = useState('');
  const [itemDetailTab, setItemDetailTab] = useState('songs');
  const [isOffline, setIsOffline] = useState(false);

  // ── Swipe navigation (mobile) ───────────────────────────────────────────────
  // swipeTrackRef holds mutable per-gesture data without triggering re-renders.
  const swipeTrackRef = useRef({ active: false, startX: 0, startY: 0, isHoriz: false });
  const [swipeDelta,  setSwipeDelta]  = useState(0);    // live translate-X during drag
  const [springBack,  setSpringBack]  = useState(false); // animate back to 0 on failed swipe
  const [tabSlideDir, setTabSlideDir] = useState(null);  // 'left'|'right' for slide-in anim

  // Keep closeAnyOpenModalRef current so the popstate handler always knows
  // which modal to close without stale-closure issues.
  useEffect(() => {
    closeAnyOpenModalRef.current = () => {
      if (isAddSectionDialogOpen) { setIsAddSectionDialogOpen(false); return true; }
      if (isEditSectionDialogOpen) { setIsEditSectionDialogOpen(false); return true; }
      if (isAddItemDialogOpen) { setIsAddItemDialogOpen(false); return true; }
      if (isAddLinkDialogOpen) { setIsAddLinkDialogOpen(false); return true; }
      if (songPickerOpen) { setSongPickerOpen(false); return true; }
      if (printOpen) { setPrintOpen(false); return true; }
      return false;
    };
  }, [isAddSectionDialogOpen, isEditSectionDialogOpen, isAddItemDialogOpen, isAddLinkDialogOpen, songPickerOpen, printOpen]);

  const isPastService = (() => {
    if (!formData.date) return false;
    const _d = new Date();
    const todayStr = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
    return formData.date < todayStr;
  })();
  const hasEditPermission = !isPastService && (formData.can_edit || canEditService(user, formData));
  const savedDataRef = useRef(null);
  const saveTimerRef = useRef(null);
  // Tracks latest formData without stale closure for Firestore listener
  const formDataRef = useRef(formData);
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [serviceId]);

  // Track online/offline status via real network probe instead of unreliable navigator.onLine.
  // While offline, re-probe every 30 s so the banner clears automatically when connectivity returns.
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const checkConnectivity = async () => {
      try {
        await api.get('/ping');
        if (!cancelled) {
          setIsOffline(false);
          if (retryTimer) { clearInterval(retryTimer); retryTimer = null; }
        }
      } catch {
        if (!cancelled) {
          setIsOffline(true);
          if (!retryTimer) {
            retryTimer = setInterval(checkConnectivity, 30000);
          }
        }
      }
    };

    checkConnectivity();

    const handleOnline = () => checkConnectivity();
    const handleOffline = () => {
      setIsOffline(true);
      if (!retryTimer) retryTimer = setInterval(checkConnectivity, 30000);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      cancelled = true;
      if (retryTimer) clearInterval(retryTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-save: fires when formData changes; also refreshes the offline cache on success.
  useEffect(() => {
    if (savedDataRef.current === null) return;
    const currentStr = JSON.stringify(formData);
    if (currentStr === savedDataRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const payload = { ...formData };
        if (!payload.name || !payload.name.trim()) payload.name = null;
        await servicesAPI.update(serviceId, payload);
        savedDataRef.current = JSON.stringify(formData);
        // Keep offline cache current after every successful save
        try {
          const raw = localStorage.getItem(`service_cache_${serviceId}`);
          if (raw) {
            const cached = JSON.parse(raw);
            localStorage.setItem(`service_cache_${serviceId}`, JSON.stringify({
              ...cached, formData, cachedAt: Date.now(),
            }));
          }
        } catch { /* quota */ }
      } catch (error) {
        toast.error('Failed to save changes');
      }
    }, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [formData, serviceId]);

  // Real-time collaborative sync — Program (order), People (team_assignments), Setlist (setlist).
  // Per-field merge: only apply an incoming field if we have no local unsaved edits for that field.
  // Also keeps the offline (localStorage) cache fresh so the Setlist is usable without network.
  useEffect(() => {
    if (!serviceId || !firestoreDb) return;
    let unsubscribe;
    try {
      const q = query(collection(firestoreDb, 'services'), where('service_id', '==', serviceId));
      unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.empty || !savedDataRef.current) return;
        const incoming = snapshot.docs[0].data();
        const savedObj = JSON.parse(savedDataRef.current);
        const current = formDataRef.current;
        const FIELDS = ['name', 'service_type', 'date', 'time', 'notes', 'setlist', 'order'];
        const updates = {};

        for (const field of FIELDS) {
          const incomingVal = incoming[field];
          if (!incomingVal) continue;
          const hasLocalEdit = JSON.stringify(current[field]) !== JSON.stringify(savedObj[field]);
          const differs = JSON.stringify(incomingVal) !== JSON.stringify(current[field]);
          if (!hasLocalEdit && differs) updates[field] = incomingVal;
        }

        // team_assignments needs special handling: even when the admin has local edits
        // (e.g. reassigning roles), we must still apply member accept/decline responses
        // from Firestore. Without this, auto-save would overwrite member responses and
        // blast notifications would incorrectly re-notify already-responded members.
        const incomingTA = incoming['team_assignments'];
        if (incomingTA) {
          const currentTA = current['team_assignments'] || [];
          const savedTA = savedObj['team_assignments'] || [];
          const hasLocalEdit = JSON.stringify(currentTA) !== JSON.stringify(savedTA);
          const differs = JSON.stringify(incomingTA) !== JSON.stringify(currentTA);
          if (differs) {
            if (!hasLocalEdit) {
              // No local edits — take the full incoming value as normal.
              updates['team_assignments'] = incomingTA;
            } else {
              // Local edits in progress — only patch in status changes from Firestore.
              // Build a memberId → status map from the incoming (authoritative) data.
              const statusMap = {};
              for (const te of incomingTA) {
                for (const a of (te.assignments || [])) {
                  if (a.member_id && a.status) statusMap[a.member_id] = a.status;
                }
              }
              if (Object.keys(statusMap).length > 0) {
                const merged = currentTA.map(te => ({
                  ...te,
                  assignments: (te.assignments || []).map(a => ({
                    ...a,
                    ...(a.member_id && statusMap[a.member_id]
                      ? { status: statusMap[a.member_id] }
                      : {}),
                  })),
                }));
                if (JSON.stringify(merged) !== JSON.stringify(currentTA)) {
                  updates['team_assignments'] = merged;
                }
              }
            }
          }
        }

        if (Object.keys(updates).length === 0) return;

        // Update savedDataRef first so auto-save doesn't re-fire for these changes
        savedDataRef.current = JSON.stringify({ ...savedObj, ...updates });
        setFormData(prev => ({ ...prev, ...updates }));

        // Keep offline cache fresh (critical for Setlist offline access)
        try {
          const raw = localStorage.getItem(`service_cache_${serviceId}`);
          if (raw) {
            const cached = JSON.parse(raw);
            localStorage.setItem(`service_cache_${serviceId}`, JSON.stringify({
              ...cached,
              formData: { ...cached.formData, ...updates },
              cachedAt: Date.now(),
            }));
          }
        } catch { /* quota */ }
      }, () => { /* Firestore unavailable — silent fail */ });
    } catch { /* Firestore not configured */ }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [serviceId]);

  // Fetch members only for specific group names and merge into `team` state.
  // Called on initial load (for groups already in service) and when a new group is added.
  const loadMembersForGroup = async (groupName) => {
    try {
      const res = await teamAPI.getAll({ group: groupName, limit: 9999 });
      const incoming = res.data.members || [];
      setTeam(prev => {
        const map = new Map(prev.map(m => [m.member_id, m]));
        incoming.forEach(m => map.set(m.member_id, m));
        return Array.from(map.values());
      });
    } catch {}
  };

  const loadData = async () => {
    tabRestoredRef.current = false; // reset on every fresh load so tab can be restored from DB
    initialMountRef.current = true; // reset for hash handling on new service
    // Fire getUnread immediately — it's a single fast Firestore read and doesn't depend on
    // any other data. Running it now (in parallel with the main Promise.all) avoids the ~9s
    // delay that would occur if we waited for loadData to fully complete first.
    if (getUnreadAbortRef.current) getUnreadAbortRef.current.cancelled = true;
    const abort = { cancelled: false };
    getUnreadAbortRef.current = abort;
    serviceChatAPI.getUnread(serviceId)
      .then(res => {
        if (!abort.cancelled) {
          setDiscUnreadCount(res.data.count || 0);
          // With hash-based tabs (Option C), the URL hash is the sole source of truth for
          // active tab state. We do NOT restore last_tab from the DB here — doing so would
          // bypass the hash convention and, on mobile, silently drop the user into the
          // full-screen chat view with no way to navigate back to the service tabs.
          // Tab state on page refresh is already preserved by the URL hash itself.
        }
      })
      .catch(() => {});
    try {
      const [serviceRes, songsRes, typesRes, rolesRes, groupsRes, assignRes] = await Promise.all([
        servicesAPI.getOne(serviceId), songsAPI.getAllForPicker(),
        serviceTypesAPI.getAll(), rolesAPI.getAll(), groupsAPI.getAll(),
        api.get('/services/my-assignments').catch(() => ({ data: [] })),
      ]);
      setSongs(songsRes.data.items || songsRes.data || []);
      setServiceTypes(typesRes.data);
      setRoles(rolesRes.data);
      setGroups(groupsRes.data);
      const map = {};
      (assignRes.data || []).forEach(a => { map[a.service_id] = a; });
      setMyAssignment(map[serviceId] || null);
      const rawOrder = (serviceRes.data.order || []).map(normalizeItem);
      if (!rawOrder.some(o => o.type === 'header')) {
        rawOrder.unshift({ id: genId(), type: 'header', title: 'Default' });
      }
      // Build role-name → sort-index map from the already-ordered roles list
      const roleOrderMap = {};
      rolesRes.data.forEach((r, i) => { roleOrderMap[r.name] = i; });
      // Re-sort each team group's assignments to reflect current role order
      const sortedTeamAssignments = (serviceRes.data.team_assignments || [])
        .filter(t => t.group && t.assignments)
        .map(entry => ({
          ...entry,
          assignments: [...(entry.assignments || [])].sort(
            (a, b) => (roleOrderMap[a.role] ?? 999) - (roleOrderMap[b.role] ?? 999)
          ),
        }));
      // Derive setlist from order items for backward compatibility (services without a stored setlist)
      const deriveSetlistFromOrder = (order) => {
        const result = [];
        const seen = new Set();
        for (const item of order) {
          if (item.type === 'item') {
            for (const s of (item.songs || [])) {
              if (!seen.has(s.song_id)) {
                seen.add(s.song_id);
                result.push({ song_id: s.song_id, key: s.key || '', tempo: s.tempo || '' });
              }
            }
          }
        }
        return result;
      };
      const storedSetlist = serviceRes.data.setlist || [];
      const initialSetlist = storedSetlist.length > 0 ? storedSetlist : deriveSetlistFromOrder(rawOrder);
      const initial = {
        name: serviceRes.data.name || '',
        service_type: serviceRes.data.service_type || '',
        date: serviceRes.data.date, time: serviceRes.data.time,
        notes: serviceRes.data.notes || '',
        order: rawOrder,
        team_assignments: sortedTeamAssignments,
        can_edit: serviceRes.data.can_edit ?? false,
        setlist: initialSetlist,
      };
      setFormData(initial);
      savedDataRef.current = JSON.stringify(initial);
      // Persist to localStorage for offline access
      try {
        localStorage.setItem(`service_cache_${serviceId}`, JSON.stringify({
          formData: initial,
          songs: songsRes.data.items || songsRes.data || [],
          cachedAt: Date.now(),
        }));
      } catch { /* storage quota exceeded — ignore */ }
      // Fetch members only for groups already in this service — await so names display immediately
      const existingGroups = sortedTeamAssignments.map(t => t.group);
      if (existingGroups.length > 0) {
        const groupResults = await Promise.all(
          existingGroups.map(g => teamAPI.getAll({ group: g, limit: 9999 }).catch(() => ({ data: { members: [] } })))
        );
        const allMembers = groupResults.flatMap(r => r.data.members || []);
        const seen = new Set();
        setTeam(allMembers.filter(m => !seen.has(m.member_id) && seen.add(m.member_id)));
      }
      // Check which assigned members are completely deleted from the system (not just moved teams)
      const assignedIds = [...new Set(
        sortedTeamAssignments.flatMap(t => (t.assignments || []).filter(a => a.member_id).map(a => a.member_id))
      )];
      if (assignedIds.length > 0) {
        api.get(`/team/members-exist?ids=${assignedIds.join(',')}`).then(res => {
          setDeletedMemberIds(new Set(res.data.deleted || []));
        }).catch(() => {});
      }
      // Fetch team availability blocks for this service's date
      const serviceDate = initial.date;
      if (serviceDate) {
        availabilityAPI.getTeam().then(res => {
          const blocks = res.data.blocks || [];
          const blocked = new Set(
            blocks
              .filter(b => b.start_date <= serviceDate && serviceDate <= (b.end_date || b.start_date))
              .map(b => b.member_id)
          );
          setBlockedMemberIds(blocked);
        }).catch(() => {});
      }
    } catch (error) {
      // Try loading from localStorage cache when offline or API fails
      try {
        const cached = localStorage.getItem(`service_cache_${serviceId}`);
        if (cached) {
          const { formData: cachedData, songs: cachedSongs } = JSON.parse(cached);
          setFormData(cachedData);
          savedDataRef.current = JSON.stringify(cachedData);
          setSongs(cachedSongs || []);
          setIsOffline(true);
        } else {
          toast.error('Failed to load service details');
        }
      } catch {
        toast.error('Failed to load service details');
      }
    }
    finally { setLoading(false); }
  };

  const genId = () => `ord_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // Wrapper that opens a program item AND pushes a history entry so the
  // browser back button returns to the program list (not /services).
  const selectItem = useCallback((itemId) => {
    if (itemId) {
      window.history.pushState({ itemDetail: true }, '');
    }
    selectedItemIdRef.current = itemId;
    setSelectedItemId(itemId);
  }, []);

  // Push a history entry when a dialog opens so browser back closes it.
  const openModalWithHistory = () => {
    window.history.pushState({ modal: true }, '');
    modalHistoryPushedRef.current = true;
  };
  // Close a dialog and cleanly pop the history entry we pushed on open.
  const closeModalCleanly = (setter) => {
    setter(false);
    if (modalHistoryPushedRef.current) {
      popstateSuppressedRef.current = true;
      modalHistoryPushedRef.current = false;
      window.history.back();
    }
  };

  const buildSections = () => {
    const sections = [];
    let current = null;
    for (const item of formData.order) {
      if (item.type === 'header') {
        current = { id: `sec-${item.id}`, header: item, items: [] };
        sections.push(current);
      } else if (item.type === 'item') {
        if (current) { current.items.push(item); }
        else if (sections.length > 0) { sections[0].items.push(item); }
      }
    }
    return sections;
  };

  const flattenSections = (sections) => {
    const order = [];
    for (const sec of sections) {
      if (sec.header) order.push(sec.header);
      order.push(...sec.items);
    }
    return order;
  };

  const handleOrderDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const sections = buildSections();
    if (type === 'SECTION') {
      const reordered = Array.from(sections);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      setFormData(prev => ({ ...prev, order: flattenSections(reordered) }));
      return;
    }
    if (type === 'ITEM') {
      const srcSecIdx = sections.findIndex(s => s.id === source.droppableId);
      const dstSecIdx = sections.findIndex(s => s.id === destination.droppableId);
      if (srcSecIdx < 0 || dstSecIdx < 0) return;
      const newSections = sections.map(s => ({ ...s, items: [...s.items] }));
      const [moved] = newSections[srcSecIdx].items.splice(source.index, 1);
      newSections[dstSecIdx].items.splice(destination.index, 0, moved);
      setFormData(prev => ({ ...prev, order: flattenSections(newSections) }));
    }
  };

  // --- Section / Item CRUD ---
  const addSection = () => {
    if (!addSectionName.trim()) return;
    setFormData(prev => ({
      ...prev, order: [...prev.order, { id: genId(), type: 'header', title: addSectionName.trim() }],
    }));
    setAddSectionName('');
    closeModalCleanly(setIsAddSectionDialogOpen);
  };

  const openEditSection = (headerId, title) => {
    setEditSectionId(headerId);
    setEditSectionName(title);
    openModalWithHistory();
    setIsEditSectionDialogOpen(true);
  };

  const updateSection = () => {
    if (!editSectionName.trim()) return;
    setFormData(prev => ({
      ...prev, order: prev.order.map(o => o.id === editSectionId ? { ...o, title: editSectionName.trim() } : o),
    }));
    closeModalCleanly(setIsEditSectionDialogOpen);
    setEditSectionId(null);
    setEditSectionName('');
  };

  const createItem = (navigateToDetail) => {
    if (!addItemName.trim() || !addItemSectionId) return;
    const itemId = genId();
    const item = { id: itemId, type: 'item', item_type: 'basic', name: addItemName.trim(), notes: addItemNotes.trim(), songs: [] };
    const newOrder = [...formData.order];
    let idx = newOrder.findIndex(o => o.id === addItemSectionId);
    if (idx >= 0) { idx++; while (idx < newOrder.length && newOrder[idx].type === 'item') idx++; newOrder.splice(idx, 0, item); }
    else newOrder.push(item);
    setFormData(prev => ({ ...prev, order: newOrder }));
    setAddItemName(''); setAddItemNotes('');
    closeModalCleanly(setIsAddItemDialogOpen);
    if (navigateToDetail) selectItem(itemId);
  };

  const removeOrderItem = (id) => {
    const item = formData.order.find(o => o.id === id);
    if (item?.type === 'header') {
      const headerCount = formData.order.filter(o => o.type === 'header').length;
      if (headerCount <= 1) { toast.error('Cannot delete the only section'); return; }
    }
    setFormData(prev => ({ ...prev, order: prev.order.filter(o => o.id !== id) }));
    if (selectedItemId === id) setSelectedItemId(null);
  };

  // --- Item detail helpers ---
  const getSelectedItem = () => formData.order.find(o => o.id === selectedItemId);

  const availableSongs = useMemo(() => {
    const item = getSelectedItem();
    const usedIds = (item?.songs || []).map(s => s.song_id);
    return songs.filter(s => !usedIds.includes(s.song_id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs, formData.order, selectedItemId]);

  // Songs already in the service setlist that are NOT yet in the selected program item
  const availableSetlistSongs = useMemo(() => {
    const item = getSelectedItem();
    const usedIds = new Set((item?.songs || []).map(s => s.song_id));
    return (formData.setlist || [])
      .filter(entry => !usedIds.has(entry.song_id))
      .map(entry => {
        const meta = songs.find(s => s.song_id === entry.song_id) || {};
        return { ...meta, song_id: entry.song_id };
      })
      .filter(s => s.title);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.setlist, songs, formData.order, selectedItemId]);

  const openSongPicker = useCallback(() => { openModalWithHistory(); setSongPickerOpen(true); }, []);

  const updateSelectedItem = (field, value) => {
    setFormData(prev => ({ ...prev, order: prev.order.map(o => o.id === selectedItemId ? { ...o, [field]: value } : o) }));
  };

  const addSongToItem = (songId) => {
    const song = songs.find(s => s.song_id === songId);
    if (!song) return;
    setFormData(prev => {
      const inSetlist = (prev.setlist || []).some(s => s.song_id === songId);
      return {
        ...prev,
        order: prev.order.map(o => {
          if (o.id !== selectedItemId) return o;
          if ((o.songs || []).some(s => s.song_id === songId)) return o;
          // Store only song_id; key/tempo come from setlist
          return { ...o, songs: [...(o.songs || []), { song_id: songId, title: song.title, artist: song.artist }], item_type: 'setlist' };
        }),
        // Add to setlist if not already there (initialise key/tempo from library)
        setlist: inSetlist ? prev.setlist : [
          ...(prev.setlist || []),
          { song_id: songId, key: song.key || '', tempo: song.bpm || '' },
        ],
      };
    });
    closeModalCleanly(setSongPickerOpen);
  };

  const removeSongFromItem = (songId) => {
    setFormData(prev => ({
      ...prev, order: prev.order.map(o => {
        if (o.id !== selectedItemId) return o;
        const ns = (o.songs || []).filter(s => s.song_id !== songId);
        return { ...o, songs: ns, item_type: ns.length > 0 ? 'setlist' : 'basic' };
      }),
    }));
  };

  // --- Attachment helpers ---
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedItemId) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await servicesAPI.uploadAttachment(serviceId, selectedItemId, file);
        setFormData(prev => ({
          ...prev, order: prev.order.map(o => {
            if (o.id !== selectedItemId) return o;
            return { ...o, attachments: [...(o.attachments || []), res.data] };
          }),
        }));
      }
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      await servicesAPI.deleteAttachment(serviceId, selectedItemId, attachmentId);
      setFormData(prev => ({
        ...prev, order: prev.order.map(o => {
          if (o.id !== selectedItemId) return o;
          return { ...o, attachments: (o.attachments || []).filter(a => a.attachment_id !== attachmentId) };
        }),
      }));
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // --- Link helpers ---
  const addLink = () => {
    let url = addLinkUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    const linkId = `link_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const link = { id: linkId, url, label: addLinkLabel.trim() || '' };
    setFormData(prev => ({
      ...prev, order: prev.order.map(o => {
        if (o.id !== selectedItemId) return o;
        return { ...o, links: [...(o.links || []), link] };
      }),
    }));
    setAddLinkUrl('');
    setAddLinkLabel('');
    closeModalCleanly(setIsAddLinkDialogOpen);
  };

  const removeLink = (linkId) => {
    setFormData(prev => ({
      ...prev, order: prev.order.map(o => {
        if (o.id !== selectedItemId) return o;
        return { ...o, links: (o.links || []).filter(l => l.id !== linkId) };
      }),
    }));
  };

  const getDisplayUrl = (url) => {
    try { return new URL(url).hostname; } catch { return url; }
  };

  const handleNotifyAll = async () => {
    flushSync(() => setNotifying(true));
    try {
      const res = await api.post(`/services/${serviceId}/notify`);
      toast.success(res.data.message || 'Notifications sent');
    } catch (e) {
      toast.error('Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  const handleNotifyChange = async (changeType) => {
    flushSync(() => setNotifyingChange(changeType));
    try {
      const res = await servicesAPI.notifyChange(serviceId, changeType);
      toast.success(res.data.message || 'Notifications sent');
    } catch (e) {
      toast.error('Failed to send notifications');
    } finally {
      setNotifyingChange(null);
    }
  };

  const handleInfoDone = async () => {
    // Flush pending auto-save immediately, then notify
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    try {
      const payload = { ...formData };
      if (!payload.name || !payload.name.trim()) payload.name = null;
      await servicesAPI.update(serviceId, payload);
      savedDataRef.current = JSON.stringify(formData);
    } catch {}
    try {
      const res = await servicesAPI.notifyChange(serviceId, 'info');
      toast.success(res.data.message || 'Team notified of info change');
    } catch {
      toast.error('Failed to notify team');
    }
  };

  const handleRespond = async (status, action) => {
    flushSync(() => setRespondingAction(action || (status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : 'accept')));
    try {
      await api.post('/services/respond', { service_id: serviceId, status });
      let label;
      if (status === 'accepted') label = 'Accepted';
      else if (status === 'declined') label = 'Declined';
      else label = myAssignment?.status === 'accepted' ? 'Unaccepted' : 'Undeclined';
      toast.success(label);
      setMyAssignment(prev => prev ? { ...prev, status } : prev);
      loadData();
    } catch (e) {
      toast.error('Failed to respond');
    } finally {
      setRespondingAction(null);
    }
  };

  // getSongMeta merges library metadata with service-level setlist key/tempo overrides
  const getSongMeta = (songId) => {
    const libSong = songs.find(s => s.song_id === songId) || {};
    const setlistEntry = (formData.setlist || []).find(s => s.song_id === songId);
    if (!setlistEntry) return libSong;
    return {
      ...libSong,
      key: (setlistEntry.key !== undefined && setlistEntry.key !== '') ? setlistEntry.key : (libSong.key || ''),
      bpm: (setlistEntry.tempo !== undefined && setlistEntry.tempo !== '') ? setlistEntry.tempo : (libSong.bpm || ''),
    };
  };

  // Build list of uniquely assigned members for @mention autocomplete
  const serviceParticipants = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const ta of formData.team_assignments) {
      for (const a of (ta.assignments || [])) {
        if (a.member_id && !seen.has(a.member_id)) {
          seen.add(a.member_id);
          const member = team.find(m => m.member_id === a.member_id);
          if (member) result.push({ name: member.name, member_id: a.member_id, picture: member.picture || null });
        }
      }
    }
    return result;
  }, [formData.team_assignments, team]);

  // Build the list of mentionable teams (e.g. "@Music Team", "@Tech Team") for the
  // Discussion tab @mention autocomplete. Each `team_assignments` entry has a `group`
  // (team name) and `assignments` (members). Skip teams with no assigned members.
  const serviceTeams = useMemo(() => {
    const teams = [];
    for (const ta of formData.team_assignments || []) {
      const groupName = (ta.group || '').trim();
      const memberIds = (ta.assignments || []).map(a => a.member_id).filter(Boolean);
      if (groupName && memberIds.length > 0) {
        teams.push({
          // Use a sentinel id with `_team_` prefix so it never collides with real member_ids.
          member_id: `_team_${groupName.toLowerCase().replace(/\s+/g, '_')}`,
          name: groupName,
          isTeam: true,
          memberCount: memberIds.length,
        });
      }
    }
    return teams;
  }, [formData.team_assignments]);

  // Lock body scroll only when on the chat tab (h-dvh layout)
  useEffect(() => {
    if (activeTab !== 'chat') return;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const resetScroll = () => { if (window.scrollY !== 0) window.scrollTo(0, 0); };
    window.addEventListener('scroll', resetScroll, { passive: true });
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('scroll', resetScroll);
    };
  }, [activeTab]);

  if (loading) {
    return (<div className="bg-slate-50"><Navbar /><div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div></div></div>);
  }

  const serviceDate = formData.date ? new Date(formData.date + 'T' + (formData.time || '00:00')) : null;
  const nameMatchesType = formData.name && formData.name.trim().toLowerCase() === formData.service_type.trim().toLowerCase();
  const sections = buildSections();

  const renderItemDetail = () => {
    const item = getSelectedItem();
    if (!item) { setSelectedItemId(null); return null; }
    return (
      <ItemDetailView
        item={item}
        hasEditPermission={hasEditPermission}
        itemDetailTab={itemDetailTab}
        setItemDetailTab={setItemDetailTab}
        updateSelectedItem={updateSelectedItem}
        setSelectedItemId={setSelectedItemId}
        getSongMeta={getSongMeta}
        setFormData={setFormData}
        selectedItemId={selectedItemId}
        openSongPicker={openSongPicker}
        removeSongFromItem={removeSongFromItem}
        uploading={uploading}
        handleFileUpload={handleFileUpload}
        removeAttachment={removeAttachment}
        openAddLinkDialog={() => { setAddLinkUrl(''); setAddLinkLabel(''); openModalWithHistory(); setIsAddLinkDialogOpen(true); }}
        removeLink={removeLink}
        formatFileSize={formatFileSize}
        getDisplayUrl={getDisplayUrl}
        onSongDragEnd={(result) => {
          if (!result.destination) return;
          const newSongs = [...(item.songs || [])];
          const [moved] = newSongs.splice(result.source.index, 1);
          newSongs.splice(result.destination.index, 0, moved);
          setFormData(prev => ({
            ...prev,
            order: prev.order.map(o => o.id === selectedItemId ? { ...o, songs: newSongs } : o)
          }));
        }}
      />
    );
  };

  // ── Touch handlers for swipe-to-change-tab ─────────────────────────────────
  const handleTouchStart = (e) => {
    // Don't intercept multi-touch gestures (pinch-to-zoom)
    if (e.touches.length > 1) {
      swipeTrackRef.current = { active: false, startX: 0, startY: 0, isHoriz: false };
      return;
    }
    swipeTrackRef.current = {
      active: activeTabRef.current !== 'chat', // disable swipe entirely when on chat tab
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      isHoriz: false,
    };
    setSpringBack(false);
    setSwipeDelta(0);
  };

  const handleTouchMove = (e) => {
    // Abort if a second finger joins mid-gesture (pinch-to-zoom)
    if (e.touches.length > 1) {
      swipeTrackRef.current.active = false;
      setSwipeDelta(0);
      return;
    }
    const s = swipeTrackRef.current;
    if (!s.active) return;
    const dx = e.touches[0].clientX - s.startX;
    const dy = e.touches[0].clientY - s.startY;
    if (!s.isHoriz) {
      if (Math.abs(dx) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) { s.active = false; setSwipeDelta(0); return; }
      s.isHoriz = true;
    }
    const max = window.innerWidth * 0.35;
    setSwipeDelta(Math.sign(dx) * Math.min(Math.abs(dx) * 0.45, max));
  };

  const handleTouchEnd = (e) => {
    const s = swipeTrackRef.current;
    if (!s.active) { setSwipeDelta(0); return; }
    s.active = false;
    const dx = e.changedTouches[0].clientX - s.startX;
    if (!s.isHoriz || Math.abs(dx) < 50) {
      setSpringBack(true);
      setSwipeDelta(0);
      setTimeout(() => setSpringBack(false), 250);
      return;
    }
    const idx = TABS.findIndex(t => t.id === activeTabRef.current);
    const rawIdx = dx < 0
      ? (idx < TABS.length - 1 ? idx + 1 : -1)
      : (idx > 0               ? idx - 1 : -1);
    // Never navigate to the chat tab via swipe — tap the tab instead
    const targetIdx = (rawIdx >= 0 && TABS[rawIdx].id === 'chat') ? -1 : rawIdx;
    if (targetIdx >= 0) {
      tabRestoredRef.current = true;
      setTabSlideDir(dx < 0 ? 'left' : 'right');
      navigate(`#${TABS[targetIdx].id}`);
      setTimeout(() => setTabSlideDir(null), 300);
    } else {
      setSpringBack(true);
    }
    setSwipeDelta(0);
    setTimeout(() => setSpringBack(false), 250);
  };
  // ── End swipe handlers ───────────────────────────────────────────────────────

  return (
    <div className={`${activeTab === 'chat' && isMobileView ? 'h-dvh flex flex-col' : 'pb-20 md:pb-6'} bg-slate-50`}
      style={activeTab === 'chat' && isMobileView ? { paddingBottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' } : undefined}
    >      <Navbar />
      {/* Offline banner */}
      {isOffline && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>You're offline — showing cached data. Changes cannot be saved until you reconnect.</span>
        </div>
      )}
      {/* ── Mobile chat: full-screen layout (mirrors 1-on-1 ChatPage) ── */}
      {activeTab === 'chat' && isMobileView && (
        <>
          <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center gap-3 shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
              data-testid="discussion-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 rounded-full bg-green-700 flex items-center justify-center shrink-0">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 leading-tight truncate">
                {formData.service_type || 'Service'}{!nameMatchesType && formData.name ? ` · ${formData.name}` : ''}
              </p>
              <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
                {serviceDate && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Calendar className="w-3 h-3 shrink-0" />
                    {serviceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                )}
                {formData.time && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Clock className="w-3 h-3 shrink-0" />
                    {(() => { const [h, m] = formData.time.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const h12 = h % 12 || 12; return m ? `${h12}:${String(m).padStart(2,'0')} ${ap}` : `${h12} ${ap}`; })()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <ServiceChatTab serviceId={serviceId} participants={serviceParticipants} teams={serviceTeams} onNewMessage={() => {}} hideHeader />
          </div>
        </>
      )}

      {/* ── Normal layout: all tabs on desktop, non-chat tabs on mobile ── */}
      {!(activeTab === 'chat' && isMobileView) && (
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y pinch-zoom' }}
      >
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate('/services')} data-testid="back-to-services-btn"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm"
          ><ArrowLeft className="w-4 h-4" /> Back to Services</button>
          {isPastService && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid="past-service-badge">
              Past service — read only
            </span>
          )}
        </div>

        {/* Service identity header — visible on every tab */}
        {(formData.service_type || formData.date) && (
          <div className="mb-3 sm:mb-5" data-testid="service-identity-header">
            <h1 className="text-sm sm:text-xl font-bold text-slate-900 dark:text-white leading-tight">
              {formData.service_type || 'Service'}
              {!nameMatchesType && formData.name && (
                <span className="font-normal text-slate-500 dark:text-slate-400"> · {formData.name}</span>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 sm:gap-x-4 sm:gap-y-1 mt-0.5 sm:mt-1.5">
              {serviceDate && (
                <span className="flex items-center gap-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                  <span className="sm:hidden">
                    {serviceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="hidden sm:inline">
                    {serviceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </span>
              )}
              {formData.time && (
                <span className="flex items-center gap-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                  {(() => { const [h, m] = formData.time.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const h12 = h % 12 || 12; return m ? `${h12}:${String(m).padStart(2,'0')} ${ap}` : `${h12} ${ap}`; })()}
                </span>
              )}
              {formData.location && (
                <span className="flex items-center gap-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                  {formData.location}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6" data-testid="service-tabs">
          {TABS.map(tab => (
            <button key={tab.id}
              onClick={() => {
                tabRestoredRef.current = true; // prevent async getUnread from overriding
                navigate(`#${tab.id}`);
              }}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center sm:gap-1.5 ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <tab.icon className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'chat' && discUnreadCount > 0 && (
                <span data-testid="discussion-unread-badge" className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-green-600 text-white text-[9px] font-bold px-1">
                  {discUnreadCount > 99 ? '99+' : discUnreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        {/* Tab content — wrapped for swipe drag feedback and slide-in animation */}
        <div
          className={tabSlideDir === 'left' ? 'tab-slide-left' : tabSlideDir === 'right' ? 'tab-slide-right' : ''}
          style={{
            transform: swipeDelta !== 0 ? `translateX(${swipeDelta}px)` : undefined,
            transition: springBack ? 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
            willChange: swipeDelta !== 0 ? 'transform' : 'auto',
          }}
        >

        {activeTab === 'info' && (
          <InfoTab
            formData={formData}
            setFormData={setFormData}
            serviceDate={serviceDate}
            nameMatchesType={nameMatchesType}
            hasEditPermission={hasEditPermission}
            serviceTypes={serviceTypes}
            myAssignment={myAssignment}
            handleRespond={handleRespond}
            respondingAction={respondingAction}
            onDone={handleInfoDone}
          />
        )}

        {activeTab === 'order' && (
          selectedItemId ? renderItemDetail() : (
            <div data-testid="order-tab-content">
              {hasEditPermission ? (
                <>
                  <div className="flex flex-wrap gap-2 justify-end mb-4">
                    <Button variant="outline" size="sm" data-testid="print-program-btn" className="rounded-full gap-2"
                      onClick={() => { openModalWithHistory(); setPrintOpen(true); }}>
                      <Printer className="w-4 h-4" /><span className="hidden sm:inline">Print</span>
                    </Button>
                    <Button variant="outline" size="sm" data-testid="notify-program-change-btn" className="rounded-full gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={() => handleNotifyChange('program')} disabled={notifyingChange === 'program'}>
                      {notifyingChange === 'program'
                        ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : <Bell className="w-4 h-4" />
                      }
                      <span className="hidden sm:inline">{notifyingChange === 'program' ? 'Sending...' : 'Notify Program Change'}</span>
                    </Button>
                    <Button variant="outline" size="sm" data-testid="add-section-btn" className="rounded-full gap-2" onClick={() => { openModalWithHistory(); setIsAddSectionDialogOpen(true); }}>
                      <Layers className="w-4 h-4" /><span className="hidden sm:inline">Add Section</span>
                    </Button>
                    <Button size="sm" data-testid="add-item-btn" className="rounded-full bg-green-700 hover:bg-green-800 gap-2"
                      onClick={() => { openModalWithHistory(); setAddItemName(''); setAddItemNotes(''); const h = formData.order.find(o => o.type === 'header'); setAddItemSectionId(h ? h.id : null); setIsAddItemDialogOpen(true); }}>
                      <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Program Item</span>
                    </Button>
                  </div>

                  {formData.order.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">No program items yet. Add a section to get started.</p>
                    </div>
                  ) : (
                    <DragDropContext onDragEnd={handleOrderDragEnd}>
                      <Droppable droppableId="sections-list" type="SECTION" isDropDisabled={!hasEditPermission} isCombineEnabled={false} ignoreContainerClipping={false}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                            {sections.map((section, sIdx) => (
                              <Draggable key={section.id} draggableId={section.id} index={sIdx} isDragDisabled={!hasEditPermission}>
                                {(secDrag, secSnap) => (
                                  <div ref={secDrag.innerRef} {...secDrag.draggableProps}
                                    className={`rounded-xl border overflow-hidden transition-shadow bg-white ${secSnap.isDragging ? 'shadow-lg border-green-300' : 'shadow-sm border-slate-200'}`}
                                    data-testid={`order-section-${section.header.id}`}
                                  >
                                    <div className="flex items-center gap-2 bg-slate-800 text-white px-4 py-3" {...secDrag.dragHandleProps}>
                                      <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                                      <span className="flex-1 font-semibold text-sm">{section.header.title}</span>
                                      <button type="button" onClick={() => openEditSection(section.header.id, section.header.title)}
                                        className="p-1 text-slate-400 hover:text-white transition-colors" data-testid={`edit-section-${section.header.id}`}
                                      ><Pencil className="w-4 h-4" /></button>
                                      {sections.length > 1 && (
                                        <DeleteButton
                                          onConfirm={() => removeOrderItem(section.header.id)}
                                          className="p-1"
                                          title="Remove section"
                                          testId={`remove-section-${section.header.id}`}
                                        />
                                      )}
                                    </div>

                                    <Droppable droppableId={section.id} type="ITEM" isDropDisabled={!hasEditPermission} isCombineEnabled={false} ignoreContainerClipping={false}>
                                      {(itemsProv, itemsSnap) => (
                                        <div ref={itemsProv.innerRef} {...itemsProv.droppableProps}
                                          className={`min-h-[8px] p-2 ${itemsSnap.isDraggingOver ? 'bg-green-50' : ''}`}
                                        >
                                          {section.items.length === 0 && (
                                            <p className="text-xs text-slate-400 text-center py-3">No items in this section</p>
                                          )}
                                          {section.items.map((item, iIdx) => (
                                            <OrderItemRow
                                              key={item.id}
                                              item={item}
                                              index={iIdx}
                                              hasEditPermission={hasEditPermission}
                                              onSelect={selectItem}
                                              onRemove={removeOrderItem}
                                              getSongMeta={getSongMeta}
                                            />
                                          ))}
                                          {itemsProv.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-end mb-4">
                    <Button variant="outline" size="sm" data-testid="print-program-btn-viewer" className="rounded-full gap-2"
                      onClick={() => setPrintOpen(true)}>
                      <Printer className="w-4 h-4" /><span className="hidden sm:inline">Print</span>
                    </Button>
                  </div>
                  <ProgramViewerMode sections={sections} getSongMeta={getSongMeta} />
                </>
              )}
            </div>
          )
        )}

        {activeTab === 'people' && (
          <PeopleTab
            formData={formData}
            setFormData={setFormData}
            hasEditPermission={hasEditPermission}
            isPastService={isPastService}
            deletedMemberIds={deletedMemberIds}
            groups={groups}
            roles={roles}
            team={team}
            notifying={notifying}
            handleNotifyAll={handleNotifyAll}
            onGroupAdded={loadMembersForGroup}
            blockedMemberIds={blockedMemberIds}
          />
        )}

        {activeTab === 'chat' && (
          <div className="h-[max(200px,calc(100dvh-20rem))] flex flex-col">
            <ServiceChatTab
              serviceId={serviceId}
              participants={serviceParticipants}
              teams={serviceTeams}
              onNewMessage={() => {}}
            />
          </div>
        )}

        {activeTab === 'setlist' && (
          <SetlistTab
            serviceId={serviceId}
            setlist={formData.setlist || []}
            onSetlistChange={(newSetlist) => setFormData(prev => ({ ...prev, setlist: newSetlist }))}
            hasEditPermission={hasEditPermission}
            songs={songs}
            getSongMeta={getSongMeta}
            order={formData.order}
            onNotifySetlistChange={() => handleNotifyChange('songs')}
            notifyingSongs={notifyingChange === 'songs'}
          />
        )}
        </div>{/* end swipe wrapper */}
        </div>
      )}{/* end normal layout */}

      {/* Add Section Dialog */}
      <Dialog open={isAddSectionDialogOpen} onOpenChange={(open) => { if (!open) closeModalCleanly(setIsAddSectionDialogOpen); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Section</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Section Name *</Label>
              <Input data-testid="section-name-input" value={addSectionName} onChange={(e) => setAddSectionName(e.target.value)}
                placeholder="e.g. Opening Prayer, Praise and Worship" autoFocus />
            </div>
            <Button onClick={addSection} data-testid="submit-section-btn" className="w-full" disabled={!addSectionName.trim()}>Add Section</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog open={isEditSectionDialogOpen} onOpenChange={(open) => { if (!open) closeModalCleanly(setIsEditSectionDialogOpen); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Edit Section</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Section Name *</Label>
              <Input data-testid="edit-section-name-input" value={editSectionName} onChange={(e) => setEditSectionName(e.target.value)}
                placeholder="Section name" autoFocus />
            </div>
            <Button onClick={updateSection} data-testid="submit-edit-section-btn" className="w-full" disabled={!editSectionName.trim()}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={isAddItemDialogOpen} onOpenChange={(open) => { if (!open) closeModalCleanly(setIsAddItemDialogOpen); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Program Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Section *</Label>
              <Select value={addItemSectionId || undefined} onValueChange={(val) => setAddItemSectionId(val)}>
                <SelectTrigger data-testid="item-section-select" className="mt-1"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {formData.order.filter(o => o.type === 'header').map(h => <SelectItem key={h.id} value={h.id}>{h.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="add-item-name">Name *</Label>
              <Input id="add-item-name" data-testid="item-name-input" value={addItemName} onChange={(e) => setAddItemName(e.target.value)} placeholder="Item name" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea data-testid="item-notes-input" value={addItemNotes} onChange={(e) => setAddItemNotes(e.target.value)} rows={2} placeholder="Additional details..." />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => createItem(false)} data-testid="submit-item-btn" className="w-full" disabled={!addItemName.trim()}>Add Program Item</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Song Picker Dialog — two-tab mode: "From Setlist" first, then "From Library" */}
      <SongPickerDialog
        open={songPickerOpen}
        onOpenChange={(open) => { if (!open) closeModalCleanly(setSongPickerOpen); }}
        availableSongs={availableSongs}
        totalSongs={songs.length}
        onAddSong={addSongToItem}
        setlistSongs={availableSetlistSongs}
      />

      {/* Add Link Dialog */}
      <Dialog open={isAddLinkDialogOpen} onOpenChange={(open) => { if (!open) closeModalCleanly(setIsAddLinkDialogOpen); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Link</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="link-url">URL *</Label>
              <Input id="link-url" data-testid="link-url-input" value={addLinkUrl} onChange={(e) => setAddLinkUrl(e.target.value)}
                placeholder="https://example.com" autoFocus />
            </div>
            <div>
              <Label htmlFor="link-label">Label (optional)</Label>
              <Input id="link-label" data-testid="link-label-input" value={addLinkLabel} onChange={(e) => setAddLinkLabel(e.target.value)}
                placeholder="e.g. Chord chart, Lyrics sheet" />
            </div>
            <Button onClick={addLink} data-testid="submit-link-btn" className="w-full" disabled={!addLinkUrl.trim()}>Add Link</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Program Print Modal */}
      {printOpen && (
        <ProgramPrintModal
          formData={formData}
          sections={sections}
          getSongMeta={getSongMeta}
          onClose={() => closeModalCleanly(setPrintOpen)}
        />
      )}
    </div>
  );
};

export default ServiceDetail;
